import { apiClient } from 'api';
import { useQuery } from '@tanstack/react-query';
import type { ConversationOption } from 'components/chat/chat-messages/types';

export interface ConversationHistoryEntry {
  role: 'user' | 'assistant';
  state: string;
  content: string | ConversationResponseData | null;
  timestamp: string;
}

export type { ConversationOption } from 'components/chat/chat-messages/types';

export interface ExpectationField {
  options: string[];
  recommended: string;
}

export interface SalaryRangeField {
  min: number;
  max: number;
}

export interface Expectations {
  salary_range?: SalaryRangeField;
  employment_type?: ExpectationField;
  work_mode?: ExpectationField;
  address?: ExpectationField;
  experience_level?: ExpectationField;
  salary_range_term?: ExpectationField;
  years_of_experience?: ExpectationField;
  salary_range_currency?: ExpectationField;
  experience_environment?: ExpectationField;
  [key: string]: any;
}

export interface UIHints {
  show_typing_indicator?: boolean;
  show_upload?: boolean;
  show_jd_preview?: boolean;
  editable?: boolean;
  show_expectations?: boolean;
  expectations?: Expectations;
  [key: string]: unknown;
}

export interface ConversationResponseData {
  message?: string;
  options?: ConversationOption[];
  ui_hints?: UIHints;
}

export interface UploadedCV {
  name: string;
  text: string;
  uploaded_at: string; // ISO datetime
}

// Represents one candidate’s final summary
export interface FinalSummary {
  name: string;
  fit_score: number;
  key_strengths: string[];
  risks_or_gaps: string[];
  recommendation: string;
}

// Represents one AI analysis result (similar structure)
export interface AnalysisResult {
  name: string;
  fit_score: number;
  key_strengths: string[];
  risks_or_gaps: string[];
  recommendation: string;
}

// The main context data structure
export interface ContextData {
  uploaded_cvs: UploadedCV[];
  final_summary: FinalSummary[];
  job_description: string;
  analysis_results: AnalysisResult[];
}

export interface ConversationHistoryResponse {
  message: string;
  data: {
    conversation_id: number;
    assessment_id: string;
    current_state: string;
    conversation_history: ConversationHistoryEntry[];
    response?: ConversationResponseData;
    context_data?: ContextData;
  };
}


// ✅ Unified params for assistant or assessment
export interface ConversationHistoryParams {
  id: string | null;
  type: 'assistant' | 'assessment';
  assessmentStatus?: string;
  isInitialized?: boolean;
}

export const conversationHistoryService = {
  getAll: async (params: { id: string | null; type: 'assistant' | 'assessment' }): Promise<ConversationHistoryResponse> => {
    if (!params.id) throw new Error('ID is required to fetch conversation history');

    const paramKey = params.type === 'assistant' ? 'assistant_id' : 'assessment_id';

    const response = await apiClient.get<ConversationHistoryResponse>('/assessment/conversation-history/', {
      params: { [paramKey]: params.id },
    });

    return response.data;
  },
};

/**
 * ✅ Hook for fetching conversation history
 */
export const useConversationHistory = ({
  id,
  type,
  assessmentStatus,
  isInitialized,
}: ConversationHistoryParams) => {
  return useQuery({
    queryKey: ['conversationHistory', type, id],
    queryFn: () => conversationHistoryService.getAll({ id, type }),
    enabled: !!id && assessmentStatus !== 'initial' && !isInitialized,
    // The global default is staleTime 60s, which makes re-opening a conversation
    // (navigate away → back) serve cached history that predates the messages just
    // sent — so the chat "doesn't continue from where you left off" until a full
    // reload wipes the cache. Always refetch fresh history when a conversation mounts.
    staleTime: 0,
    refetchOnMount: 'always',
  });
};
